export interface EwsSimulation { WarehouseID: number | null; MonthStart: string; Bags: number; }
const key = (id: any) => id == null ? 'ALL' : String(id);
const day = (v: any) => v ? String(v).substring(0, 10) : null;
const month = (v: any) => day(v)?.substring(0, 7);

/** Purchases arrive in the chosen month. ALL is unallocated; site purchases also roll up to ALL. */
export function simulateEws(base: any, inputs: EwsSimulation[]): any {
  if (!inputs.length) return base;
  const d = { ...base, Header: { ...base.Header }, Warehouses: (base.Warehouses || []).map((w: any) => ({ ...w })) };
  const byWarehouse = new Map<string, any[]>();
  for (const row of base.Waterfall || []) {
    const k = key(row.WarehouseID);
    if (!byWarehouse.has(k)) byWarehouse.set(k, []);
    byWarehouse.get(k)!.push(row);
  }
  const all = byWarehouse.get('ALL') || [];
  for (const id of [...new Set(inputs.map(s=>s.WarehouseID).filter(id=>id!=null))]) {
    if (byWarehouse.has(key(id))) continue;
    byWarehouse.set(key(id), all.map(r=>({...r,WarehouseID:id,OpeningBags:0,ClosingBags:0,ArrivalsSold:0,ArrivalsUnsold:0,GoalBags:0})));
    d.Warehouses.push({WarehouseID:id,WarehouseName:id===37?'NJ / NJX':id===31?'WI':'FL',BurnUsed:0,GoalBags:0,SpotOnHand:0,SpotReserved:0,SpotUnsold:0,ForwardBags:0,ForwardUnsold:0});
  }
  d.Waterfall = [];
  for (const [k, rows] of byWarehouse) {
    let cumulative = 0;
    for (const row of [...rows].sort((a,b) => a.MonthIndex-b.MonthIndex)) {
      const bags = inputs.filter(s => (k === 'ALL' || key(s.WarehouseID) === k) && month(s.MonthStart) === month(row.MonthStart))
        .reduce((n,s) => n + Number(s.Bags || 0), 0);
      const opening = Number(row.OpeningBags) + cumulative;
      cumulative += bags;
      d.Waterfall.push({ ...row, OpeningBags: opening, SimulationBags: bags, ClosingBags: Number(row.ClosingBags) + cumulative });
    }
    const w = d.Warehouses.find((w: any) => key(w.WarehouseID) === k);
    if (!w) continue;
    const grid = d.Waterfall.filter((r: any) => key(r.WarehouseID) === k);
    const goal = Number(w.GoalBags) || 0;
    const firstNegative = grid.find((r: any) => r.ClosingBags < 0);
    const covered = goal <= 0 ? null : firstNegative
      ? firstNegative.MonthIndex - 1 + Math.max(0, Math.min(1, (firstNegative.ClosingBags + goal) / goal)) : grid.length;
    w.MonthsCovered = covered === null ? null : Math.round(covered * 10) / 10;
    w.DepletionMonth = firstNegative?.MonthStart ?? null;
    w.CoverageStatus = covered === null ? 'No Burn' : covered < 2 ? 'Immediate Action' : covered < 4 ? 'Purchase Soon' : covered < 6 ? 'Watch List' : 'Covered';
    w.MaxDeficit10 = Math.round(Math.max(0, ...grid.filter((r: any) => r.MonthIndex <= 10).map((r: any) => -r.ClosingBags)));
    w.SpotStatus = grid.filter((r: any) => r.MonthIndex <= 3).every((r: any) => r.ClosingBags >= 0) ? 'C' : Number(w.SpotUnsold) >= .1 * Number(w.BurnUsed) ? 'NC/SA' : 'NC/NS';
    const cropClosing = (crop: any) => {
      if (!crop) return null;
      const hit = grid.find((r: any) => month(r.MonthStart) === month(crop));
      if (hit) return hit.ClosingBags;
      const last = grid[grid.length - 1];
      if (!last || month(crop)! < month(last.MonthStart)!) return null;
      const c = new Date(day(crop)! + 'T00:00:00Z'), l = new Date(day(last.MonthStart)! + 'T00:00:00Z');
      return last.ClosingBags - goal * ((c.getUTCFullYear()-l.getUTCFullYear())*12+c.getUTCMonth()-l.getUTCMonth());
    };
    const one = cropClosing(base.Header?.Crop1Arrival), two = cropClosing(base.Header?.Crop2Arrival);
    w.NeedBeforeCrop1 = Math.round(Math.max(0, -(one ?? 0)));
    w.NeedBeforeCrop2 = Math.round(Math.max(0, -(two ?? 0) - w.NeedBeforeCrop1));
    w.ActionNeeded = w.MaxDeficit10 > 0 ? `Purchase/Transfer up to ${w.MaxDeficit10} bags` : 'N/A';
    if (firstNegative) {
      const order = new Date(day(firstNegative.MonthStart)! + 'T00:00:00Z');
      order.setUTCDate(order.getUTCDate() - Number(base.Header?.CropFreightDays || 0));
      w.OrderByDate = [order.toISOString().substring(0,10), day(base.Header?.ForecastDate) || ''].sort().pop();
    } else w.OrderByDate = null;
    if (k === 'ALL') Object.assign(d.Header, w);
  }
  return d;
}
